/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: serviceable-languages-grid-component.ts
 *
 * Created by randysorensen on 2019-04-12.
 */

function ServiceableLanguagesGridController (
    this: JS5Object,
    _: JS5Object,
    $attrs: JS5Object,
    $q: JS5Object,
    $scope: JS5Object,
) {
    let rowColumns = [
        {fieldProperty: 'cCEnabled', label: 'CC'},
        {fieldProperty: 'subtitleEnabled', label: 'Subtitle'},
        {fieldProperty: 'audioEnabled', label: 'Audio Sidecar'},
        {fieldProperty: 'artworkEnabled', label: 'Artwork'},
    ];

    const getRowFields = () => _.map(rowColumns, 'fieldProperty');


    //======//
    // Grid //
    //======//

    let gridOptionsReadyDeferred = $q.defer();
    let gridApiReadyDeferred = $q.defer();

    let gridOptionsReadyPromise = gridOptionsReadyDeferred.promise;
    let gridApiReadyPromise = gridApiReadyDeferred.promise;

    //
    // This promise will resolve when the grid is ready for data
    // and API calls.
    //
    let gridReadyPromise = $q.all([
        gridOptionsReadyPromise,
        gridApiReadyPromise
    ]);

    this.onSelectRadioToggled = (nowCanSelectLanguages: boolean) => {
        this.canSelectLanguages = nowCanSelectLanguages;
        this.isRadioGroupEmpty = false;
        if (!nowCanSelectLanguages) {
            this.gridApi.selection.clearSelectedRows();
            setAllRowFields(false);
        }
        this.onServiceableLanguagesChanged({validServiceableLanguages: this.getValidServiceableLanguages()});
    };

    const toggleRowFields = (row: JS5Object) => {
        let rowFields = getRowFields();
        _.forEach(rowFields, (rowField: string) => {
            row.entity[rowField] = row.isSelected;
        });
    };

    const setAllRowFields = (nowSelected: boolean) => {
        _.forEach(this.gridOptions.data, (rowEntity: JS5Object) => {
            toggleRowFields({
                entity: rowEntity,
                isSelected: nowSelected
            });
        });
    };

    //
    // Registered with the Grid API as the row selection handler.
    //
    // Selecting a row is a shortcut for setting/clearing all
    // of the row's language requirements.
    //
    this.onRowSelectionToggled = (row: JS5Object) => {
        toggleRowFields(row);
        this.onServiceableLanguagesChanged({validServiceableLanguages: this.getValidServiceableLanguages()});
    };

    const createGridColumn = (rowColumn: JS5Object) => {
        return {
            cellTemplate: 'components/grids/serviceable-languages-grid/cell-templates/check-mark-template.html',
            displayName: rowColumn.label,
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            field: rowColumn.fieldProperty,
            name: `${rowColumn.fieldProperty}Col`,
            width: '*',
        }
    };

    const createGridOptions = () => {
        let colDefs = <JS5Object>[
            {
                cellTemplate: 'components/visual/grid/check-mark-template.html',
                enableColumnMenu: false,
                enableSorting: false,
                enableColumnResizing: false,
                field: 'checkMark',
                name: 'checkMarkRowHeaderCol',
                headerCellTemplate: 'components/grids/serviceable-languages-grid/cell-templates/header-check-mark-row-count-template.html',
                width: 55,
            },
            {
                cellTemplate: 'components/grids/serviceable-languages-grid/cell-templates/language-name-template.html',
                displayName: 'Language',
                field: 'language.name',
                name: 'languageCol',
                width: 200,
            }
        ];

        _.forEach(rowColumns, (rowColumn: JS5Object) => {
            colDefs.push(createGridColumn(rowColumn));
        });

        return {
            selectionRowHeaderColName: 'checkMarkRowHeaderCol',
            rowSelection: true,
            enableRowHeaderSelection: false,
            enableSelectAll: true,
            enableColumnResizing: false,
            enablePaginationControls: false,
            enableFullRowSelection: true,
            enableSorting: true,
            rowHeight: 26,
            multiSelect: true,
            minRowsToShow: 7,
            columnDefs: colDefs,
            data: <JS5Object>[],
            onRegisterApi: (gridApi: JS5Object) => {
                this.gridApi = gridApi;
                this.gridApi.selection.on.rowSelectionChanged(null, this.onRowSelectionToggled);
                gridApiReadyDeferred.resolve(true);
            }
        };
    };


    //================//
    // Template Logic //
    //================//

    //
    // Determine whether each and every one of the rows in the grid
    // is selected.
    //
    this.areAllRowsSelected = (): boolean => {
        return this.gridApi.selection.getSelectedCount() === this.gridOptions.data.length;
    };

    //
    // ng-click handler for the "Select All" checkbox in the top
    // right corner of the grid.
    //
    this.onSelectAllToggled = ($event: JS5Object) => {
        let allSelected = this.areAllRowsSelected();

        if (allSelected) {
            this.gridApi.selection.clearSelectedRows($event);
        } else {
            this.gridApi.selection.selectAllRows();
        }

        // toggle each row's selection flag
        setAllRowFields(!allSelected);
        this.onServiceableLanguagesChanged({validServiceableLanguages: this.getValidServiceableLanguages()});
    };

    const areAnyEntityRequirementsChecked = (rowEntity: JS5Object): boolean => {
        let rowFields = getRowFields();
        return _.some(rowFields, (rowField: string) => {
            return rowEntity[rowField] === true;
        });
    };

    //
    // Determine whether all of a row's language requirements are
    // checked. Used to determine whether the row itself should
    // appear selected in the far left column.
    //
    const areAllRowRequirementsChecked = (row: JS5Object): boolean => {
        let rowFields = getRowFields();
        let allRowFieldsChecked = true;
        _.forEach(rowFields, (rowField: string) => {
            if (!allRowFieldsChecked || !row.entity[rowField]) {
                allRowFieldsChecked = false;
            }
        });
        return allRowFieldsChecked;
    };

    //
    // ng-click handler for each of the language requirement
    // checkboxes in the grid.
    //
    this.checkMarkCellClicked = (row: JS5Object, col: JS5Object, $event: JS5Object) => {
        $event.stopImmediatePropagation();
        $event.target.blur();

        row.entity[col.colDef.field] = !row.entity[col.colDef.field];
        this.onServiceableLanguagesChanged({validServiceableLanguages: this.getValidServiceableLanguages()});

        if (!!row.isSelected !== areAllRowRequirementsChecked(row)) {
            // Only call row.setSelected() when the selection flag
            // will be changed. A ui-grid bug causes the selection
            // count to drift otherwise.
            row.setSelected(areAllRowRequirementsChecked(row));
        }
    };

    //
    // Called when the `serviceableLanguages` binding has been
    // updated. Ensures that the row selection indicators are
    // updated after assigning the data to the grid.
    //
    const onUpdateServiceableLanguages = (serviceableLanguages: JS5Object[]) => {
        this.gridOptions.data = serviceableLanguages;
        this.canSelectLanguages = _.isNull(this.canSelectLanguages) ? null : false;

        let rowsToSelect = <JS5Object>[];
        _.forEach(this.gridOptions.data, (rowEntity: JS5Object) => {
            if (!this.canSelectLanguages && areAnyEntityRequirementsChecked(rowEntity)) {
                this.canSelectLanguages = true;
                this.isRadioGroupEmpty = false;
            }
            if (areAllRowRequirementsChecked({entity: rowEntity})) {
                rowsToSelect.push(rowEntity);
            }
        });

        if (rowsToSelect.length) {
            this.gridApi.grid.modifyRows(this.gridOptions.data);
            _.forEach(rowsToSelect, (rowEntity: JS5Object) => {
                this.gridApi.selection.selectRow(rowEntity);
            });
        }
    };


    //==============//
    // Output Logic //
    //==============//

    this.isValid = (): boolean => {
        return !_.isNull(this.canSelectLanguages) && (
            this.canSelectLanguages === false ||
            _.some(this.gridOptions.data, (rowEntity: JS5Object) => {
                return areAnyEntityRequirementsChecked(rowEntity);
            })
        );
    };

    this.getValidServiceableLanguages = () => {
        if (this.isValid()) {
            return _.filter(this.gridOptions.data, areAnyEntityRequirementsChecked);
        } else if (_.isNull(this.canSelectLanguages)) {
            return undefined;
        } else {
            return null;
        }
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onChanges = (changes: JS5Object) => {
        if (changes.isRadioGroupEmpty && this.isRadioGroupEmpty) {
            this.canSelectLanguages = null;
        }
        gridReadyPromise.then(() => {
            if (changes.isRadioGroupEmpty && this.isRadioGroupEmpty) {
                this.gridApi.selection.clearSelectedRows();
                setAllRowFields(false);
                this.onServiceableLanguagesChanged({validServiceableLanguages: this.getValidServiceableLanguages()});
            } else {
                this.gridApi.selection.clearSelectedRows();
                onUpdateServiceableLanguages(this.serviceableLanguages);
            }
        });
    };

    this.$onInit = () => {
        this.gridApi = null;
        this.canSelectLanguages = this.isRadioGroupEmpty ? null : false;
        this.gridOptions = createGridOptions();

        gridOptionsReadyDeferred.resolve(true);
    };
}

angular.module('prism-ui.components').component(
    'serviceableLanguagesGrid',
    {
        bindings: {
            onServiceableLanguagesChanged: '&',
            serviceableLanguages: '<',
            isRadioGroupEmpty: '<',
            isDisabled: '<ngDisabled'
        },
        controller: ServiceableLanguagesGridController,
        templateUrl: 'components/grids/serviceable-languages-grid/serviceable-languages-grid-template.html'
    });
